import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../../../Constants/ApiEndpoints';
import { toast } from 'react-toastify';
import { FaEdit, FaTrash, FaPlus } from 'react-icons/fa';
import './PackageManagement.css';

const PackageManagement = () => {
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);

  const [currentPackage, setCurrentPackage] = useState({
    id: null,
    packageName: '',
    packageDescription: '',
    unitPrice: '',
    packageType: 'BOTH',
  });

  useEffect(() => {
    fetchPackages();
  }, []);

  const fetchPackages = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/getAllPackages`);
      setPackages(res.data || []);
    } catch (err) {
      console.error("Error fetching packages:", err);
      toast.error("Failed to load packages.");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenAddModal = () => {
    setCurrentPackage({
      id: null,
      packageName: '',
      packageDescription: '',
      unitPrice: '',
      packageType: 'BOTH',
    });
    setShowModal(true);
  };

  const handleOpenEditModal = (pkg) => {
    setCurrentPackage({
      id: pkg.id,
      packageName: pkg.packageName,
      packageDescription: pkg.packageDescription || '',
      unitPrice: pkg.unitPrice,
      packageType: pkg.packageType || 'BOTH',
    });
    setShowModal(true);
  };

  const handleDeletePackage = async (id, packageName) => {
    if (!window.confirm(`Are you sure you want to delete package "${packageName}"?`)) return;

    try {
      await axios.delete(`${API_BASE_URL}/deletePackage/${id}`);
      toast.success("Package deleted successfully!");
      fetchPackages();
    } catch (err) {
      console.error("Error deleting package:", err);
      toast.error("Failed to delete package.");
    }
  };

  const handleSavePackage = async () => {
    if (!currentPackage.packageName.trim() || !currentPackage.unitPrice || parseFloat(currentPackage.unitPrice) <= 0) {
      toast.error("Please enter a valid Package Name and Price.");
      return;
    }

    setSaving(true);
    try {
      await axios.post(`${API_BASE_URL}/savePackage`, {
        id: currentPackage.id,
        packageName: currentPackage.packageName,
        packageDescription: currentPackage.packageDescription,
        unitPrice: parseFloat(currentPackage.unitPrice),
        packageType: currentPackage.packageType,
      });
      toast.success(currentPackage.id ? "Package updated successfully!" : "Package created successfully!");
      setShowModal(false);
      fetchPackages();
    } catch (err) {
      console.error("Error saving package:", err);
      toast.error("Failed to save package.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="package-mgmt-container">
      <div className="package-mgmt-header">
        <h2>📦 OP / IP Medical Packages Management</h2>
        <button className="add-pkg-btn" onClick={handleOpenAddModal}>
          <FaPlus style={{ marginRight: '6px' }} /> Add New Package
        </button>
      </div>

      {loading ? (
        <p style={{ padding: '20px' }}>Loading medical packages...</p>
      ) : (
        <div className="table-responsive">
          <table className="package-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Package Name</th>
                <th>Description</th>
                <th>Price (₹)</th>
                <th>Applicable Module</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {packages.length === 0 ? (
                <tr>
                  <td colSpan="6" style={{ textAlign: 'center', color: '#888' }}>
                    No medical packages found. Click "Add New Package" to create one.
                  </td>
                </tr>
              ) : (
                packages.map((pkg) => (
                  <tr key={pkg.id}>
                    <td>#{pkg.id}</td>
                    <td><strong>{pkg.packageName}</strong></td>
                    <td>{pkg.packageDescription || '-'}</td>
                    <td>₹{pkg.unitPrice}</td>
                    <td>
                      <span className={`type-badge ${pkg.packageType || 'BOTH'}`}>
                        {pkg.packageType || 'BOTH'}
                      </span>
                    </td>
                    <td>
                      <button className="icon-btn edit" onClick={() => handleOpenEditModal(pkg)} title="Edit Package">
                        <FaEdit />
                      </button>
                      <button className="icon-btn delete" onClick={() => handleDeletePackage(pkg.id, pkg.packageName)} title="Delete Package">
                        <FaTrash />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <div className="popup-overlay">
          <div className="popup-content animated-popup">
            <div className="popup-header">
              <h3>{currentPackage.id ? 'Edit Package' : 'Add New Medical Package'}</h3>
              <button className="close-btn" onClick={() => setShowModal(false)}>✕</button>
            </div>
            <hr />
            <div className="popup-body">
              <label>Package Name *</label>
              <input
                type="text"
                value={currentPackage.packageName}
                onChange={(e) => setCurrentPackage({ ...currentPackage, packageName: e.target.value })}
                placeholder="e.g., Cardiac Health Checkup"
              />

              <label>Package Description</label>
              <textarea
                rows="3"
                value={currentPackage.packageDescription}
                onChange={(e) => setCurrentPackage({ ...currentPackage, packageDescription: e.target.value })}
                placeholder="Brief details about tests or consultations included..."
              />

              <label>Unit Price (₹) *</label>
              <input
                type="number"
                min="0"
                value={currentPackage.unitPrice}
                onChange={(e) => setCurrentPackage({ ...currentPackage, unitPrice: e.target.value })}
                placeholder="e.g., 1500"
              />

              <label>Package Applicable Module</label>
              <select
                value={currentPackage.packageType}
                onChange={(e) => setCurrentPackage({ ...currentPackage, packageType: e.target.value })}
              >
                <option value="BOTH">Both (Outpatient & Inpatient)</option>
                <option value="OP">Outpatient Only</option>
                <option value="IP">Inpatient Only</option>
              </select>
            </div>
            <div className="popup-footer">
              <button className="save-btn" onClick={handleSavePackage} disabled={saving}>
                {saving ? "Saving..." : "Save Package"}
              </button>
              <button className="cancel-btn" onClick={() => setShowModal(false)} disabled={saving}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PackageManagement;
